import { 
  clearCartForStoreChange, 
  findOfferByType, 
  addToCartDB 
} from './cart-db';
import { cartService } from '@/services/cart';
import type { CartItem, CartStore, OrderType } from './cart.types';

/**
 * 장바구니에 상품 추가
 */
export const addItem = async (
  storeId: string,
  storeName: string,
  orderType: OrderType,
  item: CartItem,
  userId: string | undefined,
  state: CartStore,
  setState: (updates: Partial<CartStore>) => void,
  _getState?: () => CartStore
) => {
  try {
    // Check if switching stores
    if (state.storeId && state.storeId !== storeId) {
      // Clear existing cart in DB if switching stores
      if (userId) {
        await clearCartForStoreChange(userId);
      }
      
      // Clear local cart when switching stores
      setState({
        storeId,
        storeName,
        orderType,
        items: [item],
        pickupTime: null,
        desiredPickupTime: null,
        pickupDate: null,
        pickupMethod: 'PICKUP',
        usedPoints: 0,
      });
      
      // Add to DB if user is logged in
      if (userId) {
        const offerResult = await findOfferByType(item.productId, orderType);
        if (offerResult.success && offerResult.offerId) {
          await addToCartDB(item.productId, offerResult.offerId, item.quantity, userId);
        }
      }
      
      return { success: true, message: '이전 장바구니가 초기화되고 새 상품이 추가되었습니다.' };
    }
    
    // Check if switching order types within same store
    if (state.orderType && state.orderType !== orderType && state.storeId === storeId) {
      return { 
        success: false, 
        message: 'DIFFERENT_ORDER_TYPE',
        data: { currentOrderType: state.orderType, newOrderType: orderType }
      };
    }
    
    // Check max items (5 different products)
    const existingItem = state.items.find(i => i.productId === item.productId);
    if (!existingItem && state.items.length >= 5) {
      return { success: false, message: '장바구니에는 최대 5개의 다른 상품만 담을 수 있습니다.' };
    }
    
    // Add to DB first if user is logged in
    if (userId) {
      const offerResult = await findOfferByType(item.productId, orderType);
      if (offerResult.success && offerResult.offerId) {
        const dbResult = await addToCartDB(item.productId, offerResult.offerId, item.quantity, userId);
        
        if (!dbResult.success) {
          return { success: false, message: dbResult.error };
        }
      } else {
        return { success: false, message: offerResult.error || '상품 할인 정보를 찾을 수 없습니다.' };
      }
    }
    
    // Update local state after successful DB operation
    if (existingItem) {
      // Update quantity of existing item
      setState({
        items: state.items.map(i =>
          i.productId === item.productId
            ? { ...i, quantity: orderType === 'RESERVATION_DISCOUNT' ? i.quantity + item.quantity : Math.min(i.quantity + item.quantity, i.currentStock) }
            : i
        ),
      });
    } else {
      // Add new item
      setState({
        storeId: state.storeId || storeId,
        storeName: state.storeName || storeName,
        orderType: state.orderType || orderType,
        items: [...state.items, item],
      });
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error adding item to cart:', error);
    
    // Error recovery: If DB operation fails, still allow local cart functionality
    if (userId) {
      console.warn('DB sync failed, falling back to local-only cart operation');
      
      // Still update local state for better UX
      const existingItem = state.items.find(i => i.productId === item.productId);
      
      if (existingItem) {
        setState({
          items: state.items.map(i =>
            i.productId === item.productId
              ? { ...i, quantity: i.quantity + item.quantity }
              : i
          ),
        });
      } else {
        setState({
          storeId: state.storeId || storeId,
          storeName: state.storeName || storeName,
          orderType: state.orderType || orderType,
          items: [...state.items, item],
        });
      }
      
      return { 
        success: true, 
        message: '장바구니에 추가되었습니다. (임시 저장 - 로그인 상태를 확인해주세요)' 
      };
    }
    
    return { success: false, message: '장바구니에 추가하는데 실패했습니다.' };
  }
};

/**
 * 강제로 상품 추가 (장바구니 초기화 후 추가)
 */
export const forceAddItem = async (
  storeId: string,
  storeName: string,
  orderType: OrderType,
  item: CartItem,
  _userId: string | undefined,
  _state: CartStore,
  setState: (updates: Partial<CartStore>) => void,
  clearCart: () => Promise<void>,
  addItemAction: () => Promise<{ success: boolean; message?: string; data?: any }>
) => {
  try {
    // Clear cart first (both local and DB)
    await clearCart();
    
    // Add new item - ignore the return value to match Promise<void>
    await addItemAction();
  } catch (error) {
    console.error('Error force adding item to cart:', error);
    // Fallback to local only if DB operation fails
    setState({
      storeId,
      storeName,
      orderType,
      items: [item],
      pickupTime: null,
      desiredPickupTime: null,
      pickupDate: null,
      pickupMethod: 'PICKUP',
      usedPoints: 0,
    });
  }
};

/**
 * 상품 수량 업데이트
 */
export const updateQuantity = async (
  productId: string,
  quantity: number,
  userId: string | undefined,
  state: CartStore,
  setState: (updates: Partial<CartStore>) => void
) => {
  if (quantity < 1) return;
  
  try {
    // Update DB first if user is logged in
    if (userId) {
      const currentCart = await cartService.getCurrentCart(userId);
      if (currentCart) {
        const cartItem = currentCart.items?.find(item => item.product_id === productId);
        if (cartItem) {
          const result = await cartService.updateCartItemQuantity(cartItem.id, quantity);
          if (!result.success) {
            console.error('Failed to update cart item quantity in DB:', result.error);
            return;
          }
        }
      }
    }
    
    // Update local state after successful DB operation
    setState({
      items: state.items.map(item =>
        item.productId === productId
          ? { ...item, quantity: state.orderType === 'RESERVATION_DISCOUNT' ? quantity : Math.min(quantity, item.currentStock) }
          : item
      ),
    });
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    
    // Error recovery: Still update local state even if DB sync fails
    console.warn('DB sync failed for quantity update, updating local state only');
    setState({
      items: state.items.map(item =>
        item.productId === productId
          ? { ...item, quantity: state.orderType === 'RESERVATION_DISCOUNT' ? quantity : Math.min(quantity, item.currentStock) }
          : item
      ),
    });
  }
};

/**
 * 장바구니에서 상품 제거
 */
export const removeItem = async (
  productId: string,
  userId: string | undefined,
  state: CartStore,
  setState: (updates: Partial<CartStore>) => void
) => {
  try {
    // Remove from DB first if user is logged in
    if (userId) {
      const currentCart = await cartService.getCurrentCart(userId);
      if (currentCart) {
        const cartItem = currentCart.items?.find(item => item.product_id === productId);
        if (cartItem) {
          const result = await cartService.removeFromCart(cartItem.id);
          if (!result.success) {
            console.error('Failed to remove cart item from DB:', result.error);
            return;
          }
        }
      }
    }
    
    // Update local state after successful DB operation
    const newItems = state.items.filter(item => item.productId !== productId);
    
    if (newItems.length === 0) {
      // Clear cart if no items left
      setState({
        storeId: null,
        storeName: null,
        orderType: null,
        items: [],
        pickupTime: null,
        desiredPickupTime: null,
        pickupDate: null,
        pickupMethod: 'PICKUP',
        usedPoints: 0,
      });
    } else {
      setState({ items: newItems });
    }
  } catch (error) {
    console.error('Error removing cart item:', error);
    
    // Error recovery: Still update local state even if DB sync fails
    console.warn('DB sync failed for item removal, updating local state only');
    const newItems = state.items.filter(item => item.productId !== productId);
    
    if (newItems.length === 0) {
      // Clear cart if no items left
      setState({
        storeId: null,
        storeName: null,
        orderType: null,
        items: [],
        pickupTime: null,
        desiredPickupTime: null,
        pickupDate: null,
        pickupMethod: 'PICKUP',
        usedPoints: 0,
      });
    } else {
      setState({ items: newItems });
    }
  }
};

/**
 * 장바구니 완전 초기화
 */
export const clearCart = async (
  userId: string | undefined,
  setState: (updates: Partial<CartStore>) => void
) => {
  try {
    // Clear cart in DB first if user is logged in
    if (userId) {
      const currentCart = await cartService.getCurrentCart(userId);
      if (currentCart) {
        const result = await cartService.clearCart(currentCart.id);
        if (!result.success) {
          console.error('Failed to clear cart in DB:', result.error);
        }
      }
    }
    
    // Clear local state
    setState({
      storeId: null,
      storeName: null,
      orderType: null,
      items: [],
      pickupTime: null,
      desiredPickupTime: null,
      pickupDate: null,
      pickupMethod: 'PICKUP',
      usedPoints: 0,
    });
  } catch (error) {
    console.error('Error clearing cart:', error);
    // Still clear local state even if DB operation fails
    setState({
      storeId: null,
      storeName: null,
      orderType: null,
      items: [],
      pickupTime: null,
      desiredPickupTime: null,
      pickupDate: null,
      pickupMethod: 'PICKUP',
      usedPoints: 0,
    });
  }
};